<script setup>
import { ref, watch, nextTick, computed } from 'vue';
import Input from '@pages/Shared/form/Input.vue';
import Select from '@pages/Shared/form/Select.vue';
import Loader from '@pages/Shared/PaginationLoader.vue';
import Pagination from '@pages/Shared/Pagination.vue';
import { PhFilePdf, PhFileXls, PhEye, PhPencilSimple, PhTrash } from "@phosphor-icons/vue";
import { itemNumber } from "@/helpers.js";

const props = defineProps({
    data:       { type: Object,  default: () => ({ data: [], links: [] }) },
    columns:    { type: Array,   required: true },
    rowKey:     { type: String,  default: 'id' },
    exportable: { type: Boolean, default: false },
    actions:    { type: Object,  default: () => ({ view: true, edit: true, delete: true }) },
    expandedId: { type: [String, Number], default: null },
    filters:    { type: Array,   default: () => [] },
    variant:    { type: String,  default: 'admin' },
});

const emit = defineEmits([
    'edit', 'view', 'delete', 'export-pdf', 'export-xls', 'search', 'filter'
]);

const viewBtnClass = computed(() =>
    props.variant === 'staff'
        ? 'text-primary bg-primary/10 hover:bg-primary/20 border-primary/20'
        : 'text-primary bg-primary-50 hover:bg-primary-100 border-primary-100'
);

const searchTerm = ref('');
let searchTimeout = null;
let syncingFromParent = false;

const filtersState = ref(
    Object.fromEntries(props.filters.map(f => [f.key, f.value ?? null]))
);

watch(searchTerm, (val) => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => emit('search', val), 400);
});

watch(
    () => props.filters,
    (newFilters) => {
        syncingFromParent = true;
        newFilters.forEach(f => { filtersState.value[f.key] = f.value ?? null; });
        nextTick(() => syncingFromParent = false);
    },
    { deep: true }
);

const handleFilterChange = (key, value) => {
    if (syncingFromParent) return;

    const rawValue =
        value !== null && typeof value === 'object' && 'value' in value
            ? value.value
            : value;

    filtersState.value[key] = rawValue;

    if (key === 'section') filtersState.value.class = null;

    emit('filter', { ...filtersState.value });
};

const getValue = (row, path) =>
    path.split('.').reduce((obj, key) => obj?.[key], row);
</script>

<template>
    <div>
        <!-- Toolbar -->
        <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 gap-3">

            <!-- Filters -->
            <div class="flex flex-wrap items-center gap-2 flex-1">

                <template v-for="filter in filters" :key="filter.key">
                    <Select
                        :modelValue="filtersState[filter.key]"
                        :options="filter.options"
                        :placeholder="`Select ${filter.label.toLowerCase()}`"
                        :disabled="filter.disabled"
                        class="w-full sm:w-[160px]"
                        @update:modelValue="val => handleFilterChange(filter.key, val)"
                    />
                </template>

            </div>

            <!-- Search + Export -->
            <div
                class="flex flex-col sm:flex-row gap-2
                       lg:justify-end lg:flex-1"
            >
                <Input
                    v-model="searchTerm"
                    placeholder="Search..."
                    class="w-full sm:w-64"
                />

                <div v-if="exportable" class="flex gap-2 items-center">

                    <PhFileXls
                        :size="28"
                        class="text-green-600 cursor-pointer hover:text-green-700 transition-colors"
                        @click="emit('export-xls')"
                    />

                    <PhFilePdf
                        :size="28"
                        class="text-red-500 cursor-pointer hover:text-red-600 transition-colors"
                        @click="emit('export-pdf')"
                    />

                </div>

            </div>

        </div>

        <!-- Table -->
        <div class="bg-white border border-gray-200 rounded-md overflow-hidden">

            <div class="overflow-x-auto relative">

                <Loader />

                <table class="min-w-full">

                    <thead class="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th
                                v-for="col in columns"
                                :key="col.key"
                                class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap"
                            >
                                {{ col.label }}
                            </th>
                        </tr>
                    </thead>

                    <tbody class="bg-white divide-y divide-gray-200">

                        <template v-for="(row, index) in data?.data || []" :key="row[rowKey]">

                            <tr class="transition-colors hover:bg-gray-50/80">

                                <td
                                    v-for="col in columns"
                                    :key="col.key"
                                    class="px-6 py-4 align-middle"
                                >

                                    <!-- Index -->
                                    <template v-if="col.type === 'index'">
                                        <span class="text-sm text-gray-900">
                                            {{ itemNumber(index, data) }}
                                        </span>
                                    </template>

                                    <!-- Slot -->
                                    <template v-else-if="col.type === 'slot'">
                                        <slot :name="col.key" :row="row" :index="index" />
                                    </template>

                                    <!-- Actions -->
                                    <template v-else-if="col.type === 'actions'">

                                        <div class="flex flex-wrap items-center justify-end gap-2">

                                            <button
                                                v-if="actions.view && row.can?.view"
                                                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border transition"
                                                :class="viewBtnClass"
                                                @click="emit('view', row[rowKey])"
                                            >
                                                <PhEye :size="12" /> View
                                            </button>

                                            <button
                                                v-if="actions.edit && row.can?.update"
                                                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-md border border-green-100 transition"
                                                @click="emit('edit', row[rowKey])"
                                            >
                                                <PhPencilSimple :size="12" /> Edit
                                            </button>

                                            <button
                                                v-if="actions.delete && row.can?.delete"
                                                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md border border-red-100 transition"
                                                @click="emit('delete', row[rowKey])"
                                            >
                                                <PhTrash :size="12" /> Delete
                                            </button>

                                        </div>

                                    </template>

                                    <!-- Default -->
                                    <template v-else>
                                        <span class="text-sm text-gray-900">
                                            {{ getValue(row, col.key) }}
                                        </span>
                                    </template>

                                </td>
                            </tr>

                            <tr v-if="expandedId === row[rowKey]" class="bg-gray-50">
                                <td :colspan="columns.length" class="px-6 py-4">
                                    <slot name="expanded" :row="row" :index="index" />
                                </td>
                            </tr>

                        </template>

                        <tr v-if="!data?.data || data.data.length === 0">
                            <td :colspan="columns.length" class="px-6 py-10 text-center text-sm text-gray-500">
                                No records found
                            </td>
                        </tr>

                    </tbody>

                </table>
            </div>

            <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-center sm:justify-end">
                <Pagination :links="data.links" :variant="variant" />
            </div>

        </div>

    </div>
</template>